// src/pages/CompanyPages/AppointmentLogsPage.js

import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  TextField,
  Button,
  Grid,
  Paper,
  IconButton,
  Tooltip,
  Collapse,
  MenuItem,
  InputAdornment,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useThemeMode } from "../../components/ThemeModeContext";
import { useParams } from "react-router-dom";

// Ícones
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";

// Componentes e serviços
import axiosInstance from "../../Services/axiosConfig";
import Sidebar from "../../components/CompanySidebar";
import Footer from "../../components/Footer";
import FeedbackButton from "../../components/FeedbackButton";
import FeedbackSnackbar from "../../components/FeedbackSnackbar";
import LoadingIndicator from "../../components/LoadingIndicator";

// Lógica principal
export default function AppointmentLogsPage() {
  const { companyId } = useParams();
  const token = localStorage.getItem("authToken");

  // Padrão de Tema (Dark/Light)
  const { mode } = useThemeMode();
  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          background: {
            default: mode === "light" ? "#FAF5E4" : "#121212",
            paper: mode === "light" ? "#FFFFFF" : "#1E1E1E",
          },
          text: {
            primary: mode === "light" ? "#1A202C" : "#FFFFFF",
          },
        },
      }),
    [mode]
  );

  // Estado de carregamento
  const [loading, setLoading] = useState(false);

  // Lista de appointments
  const [appointments, setAppointments] = useState([]);

  // Estado do Snackbar
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // Filtro Global
  const [searchTerm, setSearchTerm] = useState("");

  // Filtros avançados
  const [filters, setFilters] = useState({
    date: "",
    status: "",
    service: "",
    client: "",
  });

  // Controle de exibir/ocultar filtros avançados
  const [advancedOpen, setAdvancedOpen] = useState(false);

  // Lida com a mudança na barra de pesquisa global
  const handleSearchTermChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Ao pressionar Enter ou clicar no ícone de busca
  const handleSearchSubmit = () => {
    fetchAppointments();
  };

  // Fecha o Snackbar
  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  // Toggle para exibir/ocultar Filtros Avançados
  const toggleAdvanced = () => {
    setAdvancedOpen((prev) => !prev);
  };

  // Lida com mudanças nos campos avançados
  const handleAdvancedChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  // Limpar todos os filtros
  const clearFilters = () => {
    setSearchTerm("");
    setFilters({
      date: "",
      status: "",
      service: "",
      client: "",
    });
    fetchAppointments({ reset: true });
  };

  // Aplica filtros avançados
  const applyFilters = () => {
    fetchAppointments();
  };

  // Monta a busca GET
  const fetchAppointments = async ({ reset = false } = {}) => {
    setLoading(true);

    try {
      let params = {};
      if (!reset) {
        // Adiciona `filter` se houver algo digitado no searchTerm
        if (searchTerm.trim()) params.filter = searchTerm.trim();

        // Filtros avançados
        if (filters.date.trim()) params.date = filters.date.trim();
        if (filters.status.trim()) params.status = filters.status.trim();
        if (filters.service.trim()) params.service = filters.service.trim();
        if (filters.client.trim()) params.client = filters.client.trim();
      }

      // Transforma em query string
      const queryParams = new URLSearchParams(params).toString();

      const endpoint = `company-appointments-list/filter/${token}/${companyId}/?${queryParams}`;
      const response = await axiosInstance.get(endpoint);

      if (response.data.status === "success") {
        setAppointments(Object.values(response.data.data));
      } else {
        setAppointments([]);
      }
    } catch (error) {
      console.error("Error fetching appointments logs", error);
      setOpenSnackbar(true);
      setSnackbarMsg("Error fetching appointments logs");
      setSnackbarSeverity("error");
    } finally {
      setLoading(false);
    }
  };

  // Efeito ao montar para buscar inicialmente (sem filtros)
  useEffect(() => {
    fetchAppointments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Determina cor do status
  const getStatusColor = (status) => {
    switch (status) {
      case "Agendado":
        return "#4caf50";
      case "Cancelado":
        return "#f44336";
      case "Realizado":
        return "#2196f3";
      case "No-show":
        return "#ff9800";
      default:
        return "#9e9e9e";
    }
  };

  return (
    <ThemeProvider theme={theme}>
      {/* Layout horizontal: Sidebar + Conteúdo principal */}
      <Box display="flex" minHeight="100vh" bgcolor="background.default" color="text.primary">
        
        {/* Sidebar da empresa */}
        <Sidebar companyId={companyId} />

        {/* Conteúdo principal */}
        <Box flex={1} p={3}>
          <Typography variant="h4" gutterBottom>
            Appointment Logs
          </Typography>

          <Paper elevation={3} sx={{ p: 2, mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Filtros de Busca
            </Typography>

            {/* Barra de pesquisa única */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: "center",
                gap: 2,
                mb: 2,
              }}
            >
              <TextField
                label="Buscar em Todos os Campos"
                variant="outlined"
                size="small"
                fullWidth
                value={searchTerm}
                onChange={handleSearchTermChange}
                onKeyPress={(e) => {
                  if (e.key === "Enter") handleSearchSubmit();
                }}
                InputProps={{
                  endAdornment: (
                    <Tooltip title="Buscar">
                      <IconButton onClick={handleSearchSubmit} edge="end" aria-label="buscar">
                        <SearchIcon />
                      </IconButton>
                    </Tooltip>
                  ),
                }}
              />

              {/* Botão para mostrar/ocultar Filtros Avançados */}
              <Tooltip title="Filtros Avançados">
                <IconButton onClick={toggleAdvanced} aria-label="filtros-avancados">
                  <FilterListIcon />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Seção de Filtros Avançados */}
            <Collapse in={advancedOpen}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box
                  display="flex"
                  flexWrap="wrap"
                  gap={2}
                  justifyContent="flex-start"
                >
                  {/* Date */}
                  <TextField
                    label="Date"
                    type="date"
                    name="date"
                    value={filters.date}
                    onChange={handleAdvancedChange}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                  {/* Status */}
                  <TextField
                    select
                    label="Status"
                    name="status"
                    value={filters.status}
                    onChange={handleAdvancedChange}
                    size="small"
                    sx={{ width: 130 }}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="Agendado">Agendado</MenuItem>
                    <MenuItem value="Cancelado">Cancelado</MenuItem>
                    <MenuItem value="Realizado">Realizado</MenuItem>
                    <MenuItem value="No-show">No-show</MenuItem>
                  </TextField>
                  {/* Service */}
                  <TextField
                    label="Service"
                    name="service"
                    value={filters.service}
                    onChange={handleAdvancedChange}
                    size="small"
                  />
                  {/* Client */}
                  <TextField
                    label="Client"
                    name="client"
                    value={filters.client}
                    onChange={handleAdvancedChange}
                    size="small"
                  />
                </Box>

                {/* Botões de ação */}
                <Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSearchSubmit}
                    disabled={loading}
                    sx={{ minWidth: 120 }}
                  >
                    {loading ? <CircularProgress size={20} color="inherit" /> : "Apply Filters"}
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={clearFilters}
                    disabled={loading}
                    sx={{ minWidth: 120 }}
                  >
                    Clear
                  </Button>
                </Box>
              </Box>
            </Collapse>
          </Paper>

          {/* Área de resultados */}
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
              <LoadingIndicator />
            </Box>
          ) : appointments.length > 0 ? (
            <Grid container spacing={3}>
              {appointments.map((appointment, index) => (
                <Grid item xs={12} md={6} lg={4} key={index}>
                  <Box
                    p={2}
                    border={1}
                    borderColor="grey.300"
                    borderRadius={2}
                    boxShadow={2}
                  >
                    <Typography variant="h6">Service: {appointment.service}</Typography>
                    <Typography variant="body1">Client: {appointment.client}</Typography>
                    <Typography variant="body2">Email: {appointment.email}</Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: getStatusColor(appointment.status),
                        fontWeight: "bold",
                        mt: 1,
                      }}
                    >
                      Status: {appointment.status}
                    </Typography>
                    <Typography variant="body2">
                      Date: {new Date(appointment.date).toLocaleString()}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography variant="body1" mt={2}>
              No appointments found for the selected filters.
            </Typography>
          )}
        </Box>

        {/* Botão de Feedback (DarkMode + Feedback) */}
        <FeedbackButton />


        {/* Snackbar para feedback */}
        <FeedbackSnackbar
          open={openSnackbar}
          handleClose={handleCloseSnackbar}
          message={snackbarMsg}
          severity={snackbarSeverity}
        />
      </Box>
      {/* Footer */}
      <Footer />
    </ThemeProvider>
  );
}
